import type { StoryStateType } from "../types";
import type { EntityType } from "@config";
import { deleteChildEntities } from "./deleteEntity";

// Orphan-prune invariant (Tables 2.0 in-cell elements): after any tableData
// rewrite, a table's element children must all map to a live, un-merged cell.
// Children whose settings.cellId no longer resolves are removed in the same
// reducer pass — this single rule implements merge / row-delete / col-delete /
// clear-cell child cleanup with no per-action code.
export const pruneOrphanedCellChildren = (
  state: StoryStateType,
  tableId: string,
):
  | {
      entities: Record<string, EntityType>;
      indexes: Record<string, number>;
      rootIds: string[];
    }
  | undefined => {
  const table = state.flatContentLayers[tableId];
  if (!table || table.settings.name !== "Table" || !table.childIds?.length)
    return;
  const tableData = table.settings.tableData as
    | Array<{ id: string; mergedInto?: string }>
    | undefined;
  const liveCellIds = new Set<string>();
  for (const c of tableData ?? []) {
    if (!c.mergedInto) liveCellIds.add(c.id);
  }

  const indexes: Record<string, number> = {};
  table.childIds.forEach((childId, index) => {
    const child = state.flatContentLayers[childId];
    if (!child) return;
    const cellId = child.settings.cellId as string | undefined;
    if (!cellId || !liveCellIds.has(cellId)) indexes[childId] = index;
  });
  const rootIds = Object.keys(indexes);
  if (!rootIds.length) return;

  const entities: Record<string, EntityType> = {};
  rootIds.forEach((id) => {
    const child = state.flatContentLayers[id];
    if (child) deleteChildEntities(child, state.flatContentLayers, entities);
  });
  table.childIds = table.childIds.filter((id) => !(id in indexes));
  return { entities, indexes, rootIds };
};
